import { Link } from 'react-router'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { premiumFontCss } from '@root/entities/premium/ui'
import { ProfileRoute } from '@root/features/routing/model'
import { useTranslation } from '@root/shared/localization'
import { Avatar, PremiumGradientIcon } from '@root/shared/ui-kit'

import type { PremiumOwner, PremiumStatus } from '@root/entities/premium'

type Props = {
  status: PremiumStatus
  owner: PremiumOwner | undefined
  premiumTimeLeft: string | undefined
  slotResetButton: React.ReactNode
}

export const CurrencyBannerFooter = (props: Props) => {
  const { premiumTimeLeft, status, owner, slotResetButton } = props
  const { t } = useTranslation()

  return (
    <Root data-archived={status === 'archived'}>
      <Start>
        <PremiumTime>
          <PremiumGradientIconStyled
            size={13}
            data-archived={status === 'archived'}
          />
          <PremiumText data-archived={status === 'archived'}>
            {t('common.d-premium')}
          </PremiumText>
          {premiumTimeLeft && status !== 'archived' ? (
            <DateTimeLeft>{premiumTimeLeft}</DateTimeLeft>
          ) : undefined}
        </PremiumTime>
        {status === 'archived' ? (
          <ArchivedBadge>{t('premium.archived-label')}</ArchivedBadge>
        ) : undefined}
      </Start>
      {owner?.username ? (
        <PremiumOwner_
          data-archived={status === 'archived'}
          to={ProfileRoute.build({ delugeId: owner.delugeId })}
        >
          <OwnedBy data-archived={status === 'archived'}>
            {t('premium.owned-by')}:
          </OwnedBy>
          <Avatar
            size={16}
            src={owner.avatarUrl}
            fallback={owner.username.at(0)}
          />
          <OwnerName ellipsis data-archived={status === 'archived'}>
            {owner.username}
          </OwnerName>
        </PremiumOwner_>
      ) : undefined}
      {slotResetButton ? (
        <ResetButtonSlot>{slotResetButton}</ResetButtonSlot>
      ) : undefined}
    </Root>
  )
}
const ResetButtonSlot = styled.div``

const Root = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  padding: 10px;
  gap: 4px;

  font-size: 10px;
`

const Start = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
`

const PremiumTime = styled.span`
  color: var(--textA-9);

  font-size: 12px;
  line-height: 16px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;

  font-weight: 600;
`

const PremiumOwner_ = styled(Link)`
  color: var(--textA-9);

  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;

  display: grid;
  grid-auto-flow: column;
  justify-self: end;
  align-items: center;
  gap: 4px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:link,
  &:visited {
    color: var(--textA-9);
  }
`

const OwnedBy = styled.span`
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-inline-end: 4px;

  &[data-archived='true'] {
    color: var(--textA-7);
  }
  [data-archived='false'] {
    color: var(--textA-9);
  }
`

const OwnerName = styled(Typography)`
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-inline-end: 4px;

  &[data-archived='true'] {
    color: var(--textA-5);
  }
  &[data-archived='false'] {
    color: var(--textA-9);
  }

  margin: 0;
`

const DateTimeLeft = styled.div`
  color: var(--textA-9);

  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-inline-start: 4px;
`

const PremiumGradientIconStyled = styled(PremiumGradientIcon)`
  margin-right: 2px;
  margin-top: -2px;

  &[data-archived='true'] path {
    fill: var(--backgroundA-7);
  }
`

const PremiumText = styled.div`
  &[data-archived='true'] {
    color: var(--textA-7);
  }
  &[data-archived='false'] {
    ${premiumFontCss}
  }
`

const ArchivedBadge = styled.div`
  padding: 4px 6px;
  margin: -2px 0;

  border-radius: 5px;
  background: var(--background-2);

  color: var(--textA-12);
  font-size: 10px;
  font-weight: 500;
`
